<!-- Ported from the Polaris kit: src/product/components/Composer.vue.
     Classes translated to Tailwind 3 by rebranding_frontend/port-kit-component.mjs;
     strings moved to i18n by hand. Change the design in the kit first.
     Hand changes (phase 4.6): the arrow turns in RTL (`rtl:rotate-180`); the textarea undoes the app's global
     `textarea` rule (see the comment on it). -->
<script setup>
// A message field with its context and its action in one frame, so the whole thing reads as one control with
// one focus ring. Composition of the registry InputGroup. `compact` is the one-line form: no header or note,
// an icon-only submit whose name is `submitLabel`, and Enter sends.
import { ArrowRight16 } from '@carbon/icons-vue';
import {
  InputGroup,
  InputGroupAddon,
  InputGroupButton,
  InputGroupText,
  InputGroupTextarea,
} from '../ui/input-group';

const props = defineProps({
  modelValue: { type: String, default: '' },
  label: { type: String, required: true },
  submitLabel: { type: String, required: true },
  hint: { type: String, default: '' },
  note: { type: String, default: '' },
  placeholder: { type: String, default: '' },
  rows: { type: Number, default: 3 },
  compact: Boolean,
});
const emit = defineEmits(['update:modelValue', 'submit']);
const submit = () => {
  if (props.modelValue.trim()) emit('submit');
};
// The one-line form behaves like a chat field: Enter sends, Shift+Enter starts a new line. The full form keeps Enter for new lines.
const onEnter = event => {
  if (props.compact) {
    event.preventDefault();
    submit();
  }
};
</script>

<template>
  <form @submit.prevent="submit">
    <InputGroup>
      <InputGroupAddon
        v-if="!compact"
        align="block-start"
        class="justify-between"
      >
        <InputGroupText class="font-medium text-polaris-foreground">
          <slot name="icon" />{{ label }}
        </InputGroupText>
        <InputGroupText v-if="hint" class="type-polaris-caption">
          {{ hint }}
        </InputGroupText>
      </InputGroupAddon>
      <!-- Polaris patch (woot, phase 4.6): the app's global `textarea` rule (field-base: margin, outline, height)
           has no reset-base escape like the inputs'; these classes undo it here (see the phase 4 record). -->
      <InputGroupTextarea
        class="m-0 h-auto outline-none hover:outline-transparent focus:outline-transparent dark:hover:outline-transparent dark:focus:outline-transparent"
        :model-value="modelValue"
        :aria-label="label"
        :placeholder="placeholder"
        :rows="rows"
        @update:model-value="value => emit('update:modelValue', value)"
        @keydown.enter.exact="onEnter"
      />
      <InputGroupAddon v-if="compact" align="inline-end" class="self-end pb-2">
        <InputGroupButton
          type="submit"
          variant="secondary"
          size="icon-sm"
          :aria-label="submitLabel"
          :disabled="!modelValue.trim()"
        >
          <ArrowRight16 aria-hidden="true" class="rtl:rotate-180" />
        </InputGroupButton>
      </InputGroupAddon>
      <InputGroupAddon v-else align="block-end" class="justify-between">
        <InputGroupText class="type-polaris-caption">{{ note }}</InputGroupText>
        <InputGroupButton
          type="submit"
          variant="default"
          size="sm"
          :disabled="!modelValue.trim()"
        >
          {{ submitLabel }}
          <ArrowRight16 aria-hidden="true" class="rtl:rotate-180" />
        </InputGroupButton>
      </InputGroupAddon>
    </InputGroup>
  </form>
</template>
